import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from './config';

function ManualAllocation() {
  const [unallocatedCalls, setUnallocatedCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedJob, setSelectedJob] = useState(null);
  const [availableASCs, setAvailableASCs] = useState([]);
  const [selectedASC, setSelectedASC] = useState('');
  const [filters, setFilters] = useState({
    brand: 'all',
    searchTerm: ''
  });
  const [ascFilters, setAscFilters] = useState({
    city: '',
    pincode: ''
  });

  useEffect(() => {
    fetchUnallocatedCalls();
    fetchBrands();
  }, [filters.brand]);

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const fetchUnallocatedCalls = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = filters.brand !== 'all' 
        ? `${API_URL}/api/jobs/unallocated?brand=${filters.brand}`
        : `${API_URL}/api/jobs/unallocated`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let data = response.data;
      if (filters.searchTerm) {
        data = data.filter(job => 
          job.job_no.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          job.customer_name?.toLowerCase().includes(filters.searchTerm.toLowerCase())
        );
      }
      
      setUnallocatedCalls(data);
    } catch (error) {
      showMessage('error', 'Failed to load unallocated calls');
    } finally {
      setLoading(false);
    }
  };

  const fetchASCs = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/jobs/asc-list`;
      const params = [];
      
      if (ascFilters.city) params.push(`city=${encodeURIComponent(ascFilters.city)}`);
      if (ascFilters.pincode) params.push(`pincode=${ascFilters.pincode}`);
      
      if (params.length) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableASCs(response.data);
    } catch (error) {
      showMessage('error', 'Failed to load ASC list');
    }
  };

  const openAllocateModal = (job) => {
    setSelectedJob(job);
    setSelectedASC('');
    setAscFilters({ city: job.city || '', pincode: job.pincode || '' });
    fetchASCs();
  };

  const handleAllocate = async () => {
    if (!selectedASC) {
      showMessage('error', 'Please select an ASC');
      return;
    }

    try {
      setAllocating(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/api/jobs/reassign`, {
        job_no: selectedJob.job_no,
        new_asc_id: selectedASC,
        reason: `Manual allocation by KAM`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showMessage('success', `Job ${selectedJob.job_no} allocated to ${availableASCs.find(a => a.id == selectedASC)?.asp_name}`);
        setSelectedJob(null);
        fetchUnallocatedCalls(); // Refresh list
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Allocation failed');
    } finally {
      setAllocating(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const [brands, setBrands] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="fade-in" style={styles.container}>
      <h1 style={styles.header}>Manual Call Allocation</h1>

      {/* Message Display */}
      {message.text && (
        <div className={`message-toast ${message.type}`} style={{ position: 'static', marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="premium-card" style={styles.filterCard}>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label style={styles.label}>Brand</label>
            <select
              value={filters.brand}
              onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
              className="premium-select"
              style={styles.select}
            >
              <option value="all">All Brands</option>
              {brands.map(brand => (
                <option key={brand.id} value={brand.name}>{brand.name}</option>
              ))}
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.label}>Search</label>
            <input
              type="text"
              placeholder="Job No or Customer Name"
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              className="premium-input"
              style={styles.input}
              onKeyPress={(e) => e.key === 'Enter' && fetchUnallocatedCalls()}
            />
          </div>
          
          <div style={styles.filterGroup}>
            <button 
              onClick={fetchUnallocatedCalls}
              className="premium-button"
              style={styles.searchButton}
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Unallocated Calls Table */}
      <div className="premium-table-container" style={styles.tableCard}>
        <h3 style={styles.tableTitle}>
          Unallocated Calls ({unallocatedCalls.length})
          <button 
            onClick={fetchUnallocatedCalls}
            className="premium-button secondary"
            style={styles.refreshButton}
          >
            ↻ Refresh
          </button>
        </h3>
        
        {loading ? (
          <div className="premium-loading">
            <div className="loading-spinner" />
          </div>
        ) : unallocatedCalls.length === 0 ? (
          <div style={styles.noData}>
            <h3>No unallocated calls</h3>
            <p>All calls have been allocated</p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table className="premium-table" style={styles.table}>
              <thead>
                <tr>
                  <th>Job No.</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Brand</th>
                  <th>Product</th>
                  <th>City</th>
                  <th>Pincode</th>
                  <th>Created Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {unallocatedCalls.map(job => (
                  <tr key={job.id} style={styles.tableRow}>
                    <td><strong style={{ color: 'var(--system-blue)' }}>{job.job_no}</strong></td>
                    <td>{job.customer_name}</td>
                    <td>{job.contact_no}</td>
                    <td><span className="premium-badge">{job.brand}</span></td>
                    <td>{job.product}</td>
                    <td>{job.city || 'N/A'}</td>
                    <td>{job.pincode}</td>
                    <td>{formatDate(job.created_at)}</td>
                    <td>
                      <button 
                        onClick={() => openAllocateModal(job)}
                        className="premium-button success"
                        style={styles.allocateButton}
                      >
                        Allocate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Allocation Modal */}
      {selectedJob && (
        <div style={styles.modalOverlay}>
          <div className="premium-card" style={styles.modal}>
            <h3 style={styles.modalTitle}>Allocate Job: {selectedJob.job_no}</h3>
            
            <div style={styles.modalJobDetails}>
              <div style={styles.modalDetailRow}>
                <span style={styles.modalDetailLabel}>Customer:</span>
                <span>{selectedJob.customer_name}</span>
              </div>
              <div style={styles.modalDetailRow}>
                <span style={styles.modalDetailLabel}>Contact:</span>
                <span>{selectedJob.contact_no}</span>
              </div>
              <div style={styles.modalDetailRow}>
                <span style={styles.modalDetailLabel}>Address:</span>
                <span>{selectedJob.address}</span>
              </div>
              <div style={styles.modalDetailRow}>
                <span style={styles.modalDetailLabel}>Pincode:</span>
                <span>{selectedJob.pincode}</span>
              </div>
              <div style={styles.modalDetailRow}>
                <span style={styles.modalDetailLabel}>City:</span>
                <span>{selectedJob.city || 'N/A'}</span>
              </div>
            </div>

            <div style={styles.ascFilters}>
              <h4>Filter ASCs</h4>
              <div style={styles.filterRowSmall}>
                <input
                  type="text"
                  placeholder="City"
                  value={ascFilters.city}
                  onChange={(e) => setAscFilters({ ...ascFilters, city: e.target.value })}
                  className="premium-input"
                  style={styles.filterInput}
                />
                <input
                  type="text"
                  placeholder="Pincode"
                  value={ascFilters.pincode}
                  onChange={(e) => setAscFilters({ ...ascFilters, pincode: e.target.value })}
                  className="premium-input"
                  style={styles.filterInput}
                />
                <button onClick={fetchASCs} className="premium-button secondary" style={styles.filterButton}>
                  Apply
                </button>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Select ASC</label>
              <select
                value={selectedASC}
                onChange={(e) => setSelectedASC(e.target.value)}
                className="premium-select"
                style={styles.select}
              >
                <option value="">-- Select ASC --</option>
                {availableASCs.map(asc => (
                  <option key={asc.id} value={asc.id}>
                    {asc.asp_name} - {asc.city} ({asc.coverage_pincode})
                  </option>
                ))}
              </select>
              {availableASCs.length === 0 && (
                <p style={styles.hint}>No ASCs found. Try adjusting filters or add ASCs to network.</p>
              )}
            </div>

            <div style={styles.modalActions}>
              <button 
                onClick={handleAllocate}
                disabled={allocating || !selectedASC}
                className="premium-button success"
                style={styles.modalButton}
              >
                {allocating ? 'Allocating...' : 'Confirm Allocation'}
              </button>
              <button 
                onClick={() => setSelectedJob(null)}
                className="premium-button secondary"
                style={styles.modalButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px',
    animation: 'fadeIn 0.3s ease'
  },
  header: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--system-label)',
    marginBottom: '32px',
    textAlign: 'center',
    letterSpacing: '-0.022em'
  },
  filterCard: {
    marginBottom: '24px',
    padding: '24px'
  },
  filterRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr auto',
    gap: '16px',
    alignItems: 'flex-end'
  },
  filterRowSmall: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--system-secondary-label)'
  },
  select: {
    height: '44px'
  },
  input: {
    height: '44px'
  },
  filterInput: {
    flex: 1,
    height: '40px'
  },
  filterButton: {
    height: '40px',
    padding: '0 16px'
  },
  searchButton: {
    height: '44px',
    marginTop: '0'
  },
  tableCard: {
    marginTop: '20px'
  },
  tableTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    fontSize: '18px',
    fontWeight: '600'
  },
  refreshButton: {
    padding: '6px 12px',
    fontSize: '13px'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    minWidth: '1000px'
  },
  tableRow: {
    transition: 'all 0.2s ease'
  },
  allocateButton: {
    padding: '6px 12px',
    fontSize: '12px',
    minWidth: '80px'
  },
  noData: {
    textAlign: 'center',
    padding: '60px',
    background: 'var(--system-fill)',
    borderRadius: '16px'
  },
  noDataIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  modal: {
    width: '90%',
    maxWidth: '600px',
    padding: '30px',
    maxHeight: '80vh',
    overflowY: 'auto',
    animation: 'slideUp 0.3s ease'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  modalJobDetails: {
    background: 'var(--system-fill)',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '20px'
  },
  modalDetailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid var(--system-separator)',
    fontSize: '14px'
  },
  modalDetailLabel: {
    fontWeight: '500',
    color: 'var(--system-secondary-label)'
  },
  ascFilters: {
    marginBottom: '20px',
    padding: '12px',
    background: 'var(--system-fill)',
    borderRadius: '12px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  hint: {
    fontSize: '12px',
    color: 'var(--system-secondary-label)',
    marginTop: '8px'
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '20px'
  },
  modalButton: {
    padding: '10px 20px'
  }
};

export default ManualAllocation;